//================================================================
//  Component: Edit Quote Pane 
//================================================================

//  Purpose: This side pane allows the user to edit a quote line item

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, store the related parent Commercial task}
//    - currentQuoteReviewTask = { useState, used to store if there is a current quote review task}
//    - quoteLineItemIndex = { useState, used to store the index of the current selected quote line item}

//  Example:
//  
//    <EditQuotePane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//      task={task}
//      currentQuoteReviewTask={currentQuoteReviewTask}
//      quoteLineItemIndex={quoteLineItemIndex}
//    ></EditQuotePane>

//================================================================

//Libraries
import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../../../Library/WriteDocument';
import CreateRequest from '../../../../Library/CreateRequest';

//Images
import Loading from '../../../../Components/Images/Image_Loading_Ripple.svg';

//CSS

export default function EditQuotePane({
    paneOpen,
    setPaneOpen,
    task,
    currentQuoteReviewTask,
    quoteLineItemIndex
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    // --------------------------------------------------
    //  Form State
    // --------------------------------------------------

    const { register, handleSubmit, formState: { errors }, watch, reset, setValue, clearErrors } = useForm();

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to set pane status > 'onload', 'pending', 'error'
    const [paneStatus, setPaneStatus] = useState('onload');

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Submit Handler 
    const onSubmit = (value) => {

        setPaneStatus('pending');

        // Save current and new quote totals
        const currentTotal = currentQuoteReviewTask?.quote[quoteLineItemIndex]?.total;
        const newTotal = parseFloat((value?.quantity * value?.unitprice).toFixed(2));

        // ----------------------------------------------------
        //  Update quote line item in quote array
        // ----------------------------------------------------

        const updatedQuote = {
            'description': value?.description,
            'quantity': value?.quantity,
            'unitprice': value?.unitprice,
            'currency': value?.currency,
            'total': newTotal,
        }

        // Initialize quote line items if it's undefined or null
        currentQuoteReviewTask.quote = currentQuoteReviewTask.quote || [];

        // Update the quote line items array of quoteLineItemIndex index
        currentQuoteReviewTask.quote[quoteLineItemIndex] = updatedQuote;

        // Update 'lastmodifieddate' and 'lastmodifiedby'
        currentQuoteReviewTask.lastmodifieddate = new Date();
        currentQuoteReviewTask.lastmodifiedby = {
            'email': getUser?.emailaddress,
            'givenname': getUser?.givenname,
            'surname': getUser?.surname
        };

        // ------------------------------------------------------------------------
        //  Prepare Update to Agreement Record (Total Agreement Amount & Currency)
        // ------------------------------------------------------------------------

        const agreementData = {
            'agreement':  {
                'totalagreementamount': task?.agreement?.totalagreementamount - currentTotal + newTotal,
            },
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            }
        };

        // -----------------------------------------------------------------
        //  If current status is Rejected > Create NEW quote review request
        // -----------------------------------------------------------------

        if (currentQuoteReviewTask?.status === 'Rejected') {

            return CreateRequest()
            .then((requestIds) => {

                const finalPromises = [];

                // Create new quote review task based on the current rejected quote totals (only update relevant fields)
                currentQuoteReviewTask.requestid = requestIds[0];
                currentQuoteReviewTask.status = 'Draft';
                currentQuoteReviewTask.activityfeedvisible = false; // This will change to true once the status is 'For Review'
                currentQuoteReviewTask.taskdescription = '';
                currentQuoteReviewTask.rejectionreason = '';
                currentQuoteReviewTask.assigneddate = new Date();
                currentQuoteReviewTask.createddate = new Date();
                currentQuoteReviewTask.createdby = {
                    'email': getUser?.emailaddress,
                    'givenname': getUser?.givenname,
                    'surname': getUser?.surname
                };
                currentQuoteReviewTask.activityfeed = [
                    {
                        'activitydate': new Date(),
                        'actionedby': {
                            'email': getUser.emailaddress,
                            'givenname': getUser.givenname,
                            'surname': getUser.surname,
                        },
                        'action': 'created',
                        'comments': '',
                    },
                    {
                        'activitydate': new Date(Date.now() + 1000),
                        'actionedby': {
                            'email': currentQuoteReviewTask?.assignedto?.email,
                            'givenname': currentQuoteReviewTask?.assignedto?.givenname,
                            'surname': currentQuoteReviewTask.assignedto?.surname
                        },
                        'action': 'assigned',
                        'comments': '',
                    }
                ];
                currentQuoteReviewTask.comment = '';
                currentQuoteReviewTask.attachedfiles = [];

                // Create new quote
                finalPromises.push(
                    WriteDocument('requests', requestIds[0], currentQuoteReviewTask, true)
                );

                // Update total agreement amount and currency
                finalPromises.push(
                    WriteDocument('requests', task?.requestid, agreementData, true)
                );

                return Promise.all(finalPromises)
                .then(() => {
                    
                    // Reset and close side pane
                    clearErrors();
                    setPaneStatus('onload');
                    setPaneOpen(false);

                })

            })
            .catch((error) => {

                console.log('error', error);
                setPaneStatus('error');

            })

        }

        // -----------------------------------------------------------------
        // Else > Update existing quote review request
        // -----------------------------------------------------------------

        else {

            const finalPromises = [];

            // Update the current quote with updated total
            finalPromises.push(
                WriteDocument('requests', currentQuoteReviewTask?.requestid, currentQuoteReviewTask, true)
            );

            // Update total agreement amount and currency
            finalPromises.push(
                WriteDocument('requests', task?.requestid, agreementData, true)
            );

            return Promise.all(finalPromises)
            .then(() => {

                clearErrors();

                setPaneStatus('onload');
                setPaneOpen(false);

            })
            .catch((error) => {

                console.log('error', error);
                setPaneStatus('error');

            })

        }

    }

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Set all the default values on the form
    useEffect(() => {

        const quoteLineItem = currentQuoteReviewTask?.quote[quoteLineItemIndex];

        if (quoteLineItem === undefined) return;

        setValue('description', quoteLineItem?.description || '');
        setValue('quantity', quoteLineItem?.quantity || 0);
        setValue('unitprice',quoteLineItem?.unitprice || 0);
        setValue('currency',quoteLineItem?.currency || '');
        setValue('total', quoteLineItem?.total || 0);

    // eslint-disable-next-line 
    }, [currentQuoteReviewTask, quoteLineItemIndex])
    
    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // ==================================================
    //  Hide Side Pane
    // ==================================================

    if (!paneOpen) return null;

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    if (paneStatus === 'onload') {

        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container'>

                    {/* ------------------------------- */}
                    {/*  Form                           */}
                    {/* ------------------------------- */}

                    <form className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

                        {/* Header */}
                        <div className='flex flex-col gap-2 p-4'>
                            <h4 className='text-[22px] p-0 m-0'> Edit Cost Line Item</h4>
                        </div>

                        {/* Body */}
                        <div className='w-[50vw] h-full flex flex-col gap-3 overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

                            {/* ===================================== */}
                            {/*  Description                          */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Description <span className='text-[#C4314B]'>*</span> </label>
                                <input
                                    className={errors.description ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('description', {
                                        required: 'This field is required',

                                    })}
                                    type='text'
                                    placeholder='E.g. Software licenses'
                                    style={{ width: '100%' }}
                                ></input>

                                {errors.description && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.description?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Quantity                             */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Quantity <span className='text-[#C4314B]'>*</span> </label>
                                <input
                                    className={errors.quantity ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('quantity', {
                                        required: 'This field is required',
                                        pattern: {
                                            value: /^\d+(\.\d{1,2})?$/,
                                            message: 'Only numbers allowed (up to 2 decimal places)',
                                        },
                                        min: {
                                            value: 1,
                                            message: 'Minimum value is 1',
                                        },
                                    })}
                                    type='number'
                                    step='1'
                                    placeholder='Enter value'
                                ></input>

                                {errors.quantity && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.quantity?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Unit Price                           */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> 
                                    Unit Price <span className='text-[#C4314B]'>*</span>
                                    <br></br>
                                    <p className='font-light text-[15px] mt-2 mb-0'><i>Excluding Tax</i></p>
                                </label>
                                
                                <input
                                    className={errors.unitprice ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                                    {...register('unitprice', {
                                        required: 'This field is required',
                                        pattern: {
                                            value: /^\d+(\.\d{1,2})?$/,
                                            message: 'Only numbers allowed (up to 2 decimal places)',
                                        },
                                    })}
                                    type='number'
                                    step='0.01'
                                    placeholder='Enter value'
                                ></input>

                                {errors.unitprice && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.unitprice?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Currency                             */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Currency <span className='text-[#C4314B]'>*</span></label>
                                <select
                                    className={errors.currency ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                                    {...register('currency', {
                                        required: 'This field is required'
                                    })}
                                    style={{ width: '450px' }}
                                    disabled
                                >
                                    <option hidden value=''>-</option>
                                    <option value='AUD'> AUD </option>
                                    <option value='SGD'> SGD </option>
                                    <option value='GBP'> GBP </option>
                                    <option value='USD'> USD </option>
                                    <option value='EUR'> EUR </option>
                                    <option value='MYR'> MYR </option>
                                </select>

                                {errors.currency && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.currency?.message}</p>}
                            </div>

                            {/* ===================================== */}
                            {/*  Total                                */}
                            {/* ===================================== */}

                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Total </label>
                                <input
                                    className='Input-Field-Text'
                                    type='number'
                                    disabled
                                    value={(watch('unitprice') * watch('quantity')).toFixed(2)}
                                ></input>

                            </div>

                        </div>

                        {/* ------------------------------- */}
                        {/*  Submit Buttons                 */}
                        {/* ------------------------------- */}

                        <div className='flex flex-row gap-2 px-4 py-3'>

                            {/* Edit */}
                            {/* NH TODO - Disable button when there are no changes made */}
                            <button 
                                className='Primary-Button' 
                                type='submit' 
                                value='submit' 
                                disabled={
                                    errors.pricing || errors.currency || errors.comment ||
                                    (
                                        watch('description') ===  currentQuoteReviewTask?.quote[quoteLineItemIndex]?.description &&
                                        watch('quantity') ===  currentQuoteReviewTask?.quote[quoteLineItemIndex]?.quantity &&
                                        watch('unitprice') === currentQuoteReviewTask?.quote[quoteLineItemIndex]?.unitprice &&
                                        watch('currency') === currentQuoteReviewTask?.quote[quoteLineItemIndex]?.currency &&
                                        watch('total') ===  currentQuoteReviewTask?.quote[quoteLineItemIndex]?.total
                                    )
                                    ? true : false}
                            >
                                Edit
                            </button>

                            {/* Cancel */}
                            <button
                                className='Secondary-Button'
                                onClick={() => {

                                    // Reset Form and Close Side Pane
                                    setValue('description', currentQuoteReviewTask?.quote[quoteLineItemIndex]?.description || '');
                                    setValue('quantity', currentQuoteReviewTask?.quote[quoteLineItemIndex]?.quantity || 0);
                                    setValue('unitprice',currentQuoteReviewTask?.quote[quoteLineItemIndex]?.unitprice || 0);
                                    setValue('currency',currentQuoteReviewTask?.quote[quoteLineItemIndex]?.currency || '');
                                    setValue('total', currentQuoteReviewTask?.quote[quoteLineItemIndex]?.total || 0);
                                    setPaneOpen(false);
                                    clearErrors();

                                }}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </dialog>
            </div>
        );
    }

    // ---------------------------------------------------
    //  Pending
    // ---------------------------------------------------

    else if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background'>
                <dialog className='Pane-Container flex justify-center items-center text-center'>
                    <img alt='loading-circle-icon' src={Loading}></img>
                </dialog>
            </div>
        )
    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else if (paneStatus === 'error') {
        return (
            <div className='Pane-Background'>

                {/* Pane Container */}
                <dialog className='Pane-Container'>
                    <div className='flex flex-col gap-2 w-full justify-center items-center'>

                        <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
                        <h4 className='mb-0'>Oops! Something went wrong.</h4>
                        <p className='text-center leading-[1.7]'>
                            An error occurred while we processed your request.
                        </p>
                        <button
                            className='Primary-Button'
                            onClick={() => {

                                // Reset Form and Close Pane
                                setPaneOpen(false);
                                reset();
                                clearErrors();

                            }}
                        >
                            Close
                        </button>

                    </div>
                </dialog>
            </div>

        )
    }

    //------------------------------------------------------

}